import {
  clearAllBodyScrollLocks,
  disableBodyScroll,
  enableBodyScroll,
} from 'body-scroll-lock';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Waypoint } from 'react-waypoint';
import useAppSelector from '../app/redux/useAppSelector';
import Avatar from '../components/Avatar';
import SVGIcon from '../components/SVGIcon';
import { SidebarItem } from '../graphql/generated/graphqlTypes';
import draftsIcon from '../icons/prefilled/drafts.svg';
import groupIcon from '../icons/prefilled/group.svg';
import homeIcon from '../icons/prefilled/home.svg';
import templateIcon from '../icons/prefilled/template.svg';
import subtopicsIcon from '../icons/subtopics.svg';
import NotificationButton from '../notification/NotificationButton';
import InviteButton from '../organization/InviteButton';
import UIActions from '../ui/UIActions';
import isSize from '../utils/responsive/isSize';
import Sizes from '../utils/responsive/Sizes';
import useSlug from '../utils/router/useSlug';
import templatesPath, { savePreviousRoute } from '../utils/url/templatesPath';
import userPath from '../utils/url/userPath';
import ExternalPostsNav from './ExternalPostsNav';
import FavoritesTopTopics from './FavoritesTopTopics';
import styles from './Sidebar.module.scss';
import SidebarNavItem from './SidebarNavItem';
import SidebarSettingsNavItem from './SidebarSettingsNavItem';
import TeamButton from './TeamButton';
import useSidebarSettings from './useSidebarSettings';

export const BaseSidebar = () => {
  const [topicId] = useSlug('topicSlug');

  const { hiddenItems } = useSidebarSettings();

  const [isScrolled, setIsScrolled] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);

  const navsRef = useRef<HTMLDivElement>(null);

  const currentUser = useAppSelector((state) => state.session.user);
  const isOpen = useAppSelector((state) => state.ui.isSidebarOpen);
  const isModalOpen = useAppSelector((state) => state.ui.modal.isOpen);

  const dispatch = useDispatch();

  const isGuest = currentUser.type === 'GUEST';

  useEffect(() => {
    if (navsRef.current) {
      if (isSize(Sizes.SM) && isOpen && !isModalOpen) {
        disableBodyScroll(navsRef.current);
      } else {
        enableBodyScroll(navsRef.current);
      }
    }
    return clearAllBodyScrollLocks;
  }, [isOpen, isModalOpen]);

  return (
    <>
      <nav
        id="sidebar"
        className={classNames(styles.sidebar, {
          [styles.scrolled]: isScrolled,
          [styles.atBottom]: isAtBottom,
        })}
      >
        <div className={styles.teamNavRow}>
          <TeamButton />
          <NotificationButton />
        </div>
        <div className={styles.navs} ref={navsRef}>
          <Waypoint
            onEnter={() => {
              setIsScrolled(false);
            }}
            onLeave={() => {
              setIsScrolled(true);
            }}
          />
          <div className={styles.mainNavs}>
            <SidebarNavItem
              to={userPath(currentUser)}
              icon={<Avatar size="small" user={currentUser} />}
              onlyActiveOnIndex
            >
              {currentUser.name}
            </SidebarNavItem>
            <SidebarNavItem to="/" icon={homeIcon} onlyActiveOnIndex>
              Home
            </SidebarNavItem>
            <SidebarNavItem to="/drafts" icon={draftsIcon} onlyActiveOnIndex>
              Drafts
            </SidebarNavItem>
            {!hiddenItems.has(SidebarItem.Templates) && (
              <SidebarNavItem
                to={templatesPath()}
                onClick={() => {
                  savePreviousRoute();
                }}
                icon={templateIcon}
                onlyActiveOnIndex
              >
                Templates
              </SidebarNavItem>
            )}
            {!isGuest && !hiddenItems.has(SidebarItem.ContentMap) && (
              <SidebarNavItem
                to="/topics"
                icon={
                  <SVGIcon className={classNames(styles.contentMapIcon)}>
                    {subtopicsIcon}
                  </SVGIcon>
                }
                onlyActiveOnIndex
              >
                Content Map
              </SidebarNavItem>
            )}
            {!hiddenItems.has(SidebarItem.Groups) && (
              <SidebarNavItem to="/groups" icon={groupIcon}>
                Groups
              </SidebarNavItem>
            )}
            <SidebarSettingsNavItem />
          </div>
          <FavoritesTopTopics topicId={topicId} />
          <ExternalPostsNav />
          <Waypoint
            onEnter={() => {
              setIsAtBottom(true);
            }}
            onLeave={() => {
              setIsAtBottom(false);
            }}
          />
        </div>
        {!isGuest && (
          <div className={styles.inviteContainer}>
            <InviteButton block />
          </div>
        )}
      </nav>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO - fix next time this file is edited */}
      <div
        className={classNames(styles.clickOutside, {
          [styles.show]: isOpen,
        })}
        onClick={() => dispatch(UIActions.toggleSidebar())}
      />
    </>
  );
};

export default BaseSidebar;
